import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import {differenceInDays} from 'date-fns/differenceInDays';
import {parseISO} from 'date-fns/parseISO';
import {subDays} from 'date-fns/subDays';

import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewDatePickerField,
  showModal,
} from '@edna/components';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import {formatDate} from '@edna/utils';

import {LeftButton, ModalCenterImage, SecondaryText, Text} from 'src/components/primitives';
import {AreYouSureImage} from 'src/images';

import {TSubjectLicense} from './LicensesList/Item/definitions';
import {useScheduleCancelLicenseMutation} from './api';
import {TLicense} from './definitions';
import * as S from './style';

type TProps = {
  closeModal: TEmptyFunction;
  license: TLicense | TSubjectLicense;
};

const MIN_DATE = subDays(new Date(), 1);

const ConfirmModal = React.memo<{date: Date; onConfirm: TAnyFunction; onCancel: TAnyFunction}>(
  ({date, onConfirm, onCancel}) => {
    const {t} = useTranslation();

    return (
      <Modal width="430px">
        <ModalHeader>{t('Licenses:confirmModal.title')}</ModalHeader>
        <ModalContent>
          <SecondaryText>
            <Trans
              i18nKey="Licenses:confirmModal.description"
              values={{date: formatDate(date, {format: 'yyyy-MM-dd'})}}
            >
              <Text weight={600} color="blue500" isInline />
            </Trans>
          </SecondaryText>
        </ModalContent>
        <ModalCenterImage as={AreYouSureImage} />
        <ModalActions>
          <LeftButton onClick={onCancel} appearance="secondary">
            {t('Common:button.cancel2')}
          </LeftButton>
          <Button onClick={onConfirm} appearance="primary" data-selector="Licenses:button.cancel">
            {t('Common:button.toPlan')}
          </Button>
        </ModalActions>
      </Modal>
    );
  },
);

const CancelLicenseModal = React.memo<TProps>(({closeModal, license}) => {
  const {t} = useTranslation();

  const [cancelLicense] = useScheduleCancelLicenseMutation();

  const activeUntil = React.useMemo(
    () => (license.activeUntil ? parseISO(license.activeUntil) : undefined),
    [license],
  );
  const initialValues = React.useMemo(
    () => ({
      date: activeUntil,
    }),
    [activeUntil],
  );
  const methods = useForm({
    defaultValues: initialValues,
  });

  useOnChange(() => methods.reset(initialValues), [initialValues]);

  const handleConfirm = React.useCallback(
    ({date}: {date?: Date}) => {
      closeModal();

      if (!date) {
        return;
      }

      if (activeUntil && differenceInDays(activeUntil, date) > 0) {
        return showModal((innerCloseModal) => (
          <ConfirmModal
            date={date}
            onCancel={innerCloseModal}
            onConfirm={() => {
              cancelLicense({licenseId: license.id, cancelAt: date});
              innerCloseModal();
            }}
          />
        ));
      }

      cancelLicense({
        licenseId: license.id,
        cancelAt: date,
      });
    },
    [license, closeModal],
  );

  return (
    <FormProvider {...methods}>
      <Modal width="430px">
        <ModalHeader>{t('Licenses:cancelModal.title')}</ModalHeader>
        <ModalContent>
          <Stack size="3">
            <SecondaryText>{t('Licenses:cancelModal.description')}</SecondaryText>
            <Stack size="1">
              <S.SmallLabel htmlFor="date">{t('Licenses:cancelModal.selectDate')}</S.SmallLabel>
              <NewDatePickerField minDate={MIN_DATE} maxDate={activeUntil} name="date" />
            </Stack>
            <SecondaryText>{t('Licenses:cancelModal.hint')}</SecondaryText>
          </Stack>
        </ModalContent>
        <ModalActions>
          <LeftButton onClick={closeModal} appearance="secondary">
            {t('Common:button.cancel2')}
          </LeftButton>
          <Button
            onClick={methods.handleSubmit(handleConfirm)}
            appearance="primary"
            data-selector="Licenses:button.cancel"
          >
            {t('Common:button.toPlan')}
          </Button>
        </ModalActions>
      </Modal>
    </FormProvider>
  );
});

export {CancelLicenseModal};
